import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  acceptAllConsent,
  CONSENT_UPDATED_EVENT,
  DEFAULT_CONSENT,
  hasConsentChoice,
  readStoredConsent,
  rejectOptionalConsent,
  saveConsent,
} from '../utils/cookieConsent';

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CONSENT);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = useCallback(() => {
    const stored = readStoredConsent();
    setHasChoice(hasConsentChoice());
    setCategories(stored?.categories || DEFAULT_CONSENT);
  }, []);

  useEffect(() => {
    refresh();
    setReady(true);
    const onUpdate = () => refresh();
    window.addEventListener(CONSENT_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, onUpdate);
  }, [refresh]);

  const value = useMemo(() => ({
    ready,
    hasChoice,
    categories,
    settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    updateConsent: (next) => {
      saveConsent(next);
      refresh();
    },
    acceptAll: () => {
      acceptAllConsent();
      refresh();
    },
    rejectOptional: () => {
      rejectOptionalConsent();
      refresh();
    },
  }), [ready, hasChoice, categories, settingsOpen, refresh]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
